/**
 * O cartão "Distribuição de Saídas por Trimestre" do Fechamento.
 *
 * Papel: reproduz `renderChartTrimestral` (l. 3239–3334) com o Chart.js 4 do
 * npm — barras agrupadas, uma série por categoria na ordem do total no ano, um
 * grupo por trimestre até o do mês aberto, a legenda embaixo e a dica com os
 * meses somados, o valor da categoria e o total do trimestre — e o cabeçalho
 * do cartão com o ano à direita (l. 676–682).
 *
 * Como usar: os números chegam prontos em `Trimestres`, e o gráfico só
 * desenha. O que a origem decide — cores, ordem, textos da dica — mora em
 * `configuracao-do-grafico.ts`; o componente só cria o gráfico na montagem,
 * troca a configuração quando os trimestres mudam e o destrói ao sair. Só os
 * seis pedaços do Chart.js que o gráfico usa são registrados, e o resto da
 * biblioteca fica fora do bundle.
 *
 * O canvas não lê variável CSS: a borda das barras, a grade do eixo Y e a cor
 * da categoria sem cor saem de `getComputedStyle` sobre o próprio canvas, que
 * está dentro do tema. A cor de cada série é a da categoria, gravada no plano.
 * A fonte é a padrão do Chart.js, como na origem.
 *
 * Antes de imprimir, o gráfico se redimensiona pelo `beforeprint` (l. 1561):
 * sem isso, o canvas ficaria com a largura da tela e estouraria a coluna do
 * papel. No papel do Fechamento o cartão começa em página nova e não se
 * parte, e o ano do cabeçalho some (l. 266–283).
 */
import { type Trimestres } from '@concierge/shared';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { useEffect, useRef } from 'react';

import { type CoresDoGrafico, configuracaoDoGrafico } from './configuracao-do-grafico';
import { CabecalhoDoCartao, Cartao, CorpoDoCartao, TituloDoCartao } from './kit/cartao';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Legend, Tooltip);

/**
 * Lê as cores de token que o canvas usa, no tema em volta dele.
 *
 * @param elemento - O canvas, dentro do `QuadroEmbutido`.
 * @returns As três cores, como o navegador as resolveu.
 */
function lerCoresDoTema(elemento: HTMLElement): CoresDoGrafico {
  const estilo = getComputedStyle(elemento);
  return {
    reserva: estilo.getPropertyValue('--emb-cinza-texto').trim(),
    borda: estilo.getPropertyValue('--superficie').trim(),
    grade: estilo.getPropertyValue('--emb-grade-grafico').trim(),
  };
}

/**
 * O cartão do gráfico trimestral.
 *
 * @param trimestres - O gráfico do ano do mês aberto; nulo enquanto o
 *   Fechamento carrega, e então só o cartão aparece.
 * @returns O cartão com o gráfico.
 */
export function GraficoTrimestral({ trimestres }: { trimestres: Trimestres | null }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const grafico = useRef<Chart<'bar', number[], string> | null>(null);

  useEffect(() => {
    const elemento = canvas.current;
    if (elemento === null || trimestres === null) return;
    const novo = new Chart(
      elemento,
      configuracaoDoGrafico(trimestres, lerCoresDoTema(elemento)),
    );
    grafico.current = novo;
    return () => {
      novo.destroy();
      grafico.current = null;
    };
  }, [trimestres]);

  useEffect(() => {
    /** Ajusta o canvas à largura do papel antes de imprimir. */
    function handleAntesDeImprimir() {
      grafico.current?.resize();
    }
    window.addEventListener('beforeprint', handleAntesDeImprimir);
    return () => window.removeEventListener('beforeprint', handleAntesDeImprimir);
  }, []);

  return (
    <Cartao className="print:[body[data-impressao=imprimindo-fechamento]_&]:break-before-page print:[body[data-impressao=imprimindo-fechamento]_&]:break-inside-avoid">
      <CabecalhoDoCartao>
        <TituloDoCartao>Distribuição de Saídas por Trimestre</TituloDoCartao>
        <span className="text-[11px] text-texto-fraco print:[body[data-impressao=imprimindo-fechamento]_&]:hidden">
          {trimestres?.ano}
        </span>
      </CabecalhoDoCartao>
      <CorpoDoCartao>
        <div className="relative h-[280px] max-w-full">
          <canvas ref={canvas} className="!max-w-full" />
        </div>
      </CorpoDoCartao>
    </Cartao>
  );
}
